/**
 * Animated sprites made from shapes and rules rather than drawn pixel by pixel (docs/spec/04 §3):
 * the diamond's travelling glint, the firefly's pulse, the butterfly's wingbeat, the amoeba's boil,
 * explosions, the cover shimmer.
 */

import { BRICK, GEM, STEEL } from './tiles';
import { hash, paint, pixel } from './types';
import type { Frame } from './types';

/**
 * The gem twinkles: its four facets (crown and pavilion, left and right) cycle through the cave's
 * colours out of step with each other, eight frames a cycle, while the outline stays bright.
 */
export function diamondFrames(): Frame[] {
  const cycle = ['3', '2', '1', '2'];
  return [0, 1, 2, 3, 4, 5, 6, 7].map((k) =>
    paint((x, y) => {
      const p = pixel(GEM, x, y);
      if (p !== '1' && p !== '2') return p;
      const facet = (y <= 5 ? 0 : 2) + (x < 4 ? 0 : 1);
      return cycle[(facet * 3 + (k >> 1)) % 4] ?? p;
    }),
  );
}

/** Concentric squares pulsing outward. */
export function fireflyFrames(): Frame[] {
  const ring = ['3', '2', '1', '.'];
  return [0, 1, 2, 3].map((k) =>
    paint((x, y) => {
      const yy = Math.floor(y / 2);
      const r = Math.min(x, 7 - x, yy, 7 - yy);
      return ring[(r + 4 - k) % 4] ?? '.';
    }),
  );
}

/** The left half of an open butterfly, body in the last column; mirrored for the right. */
const WING = ['....', '.2..', '222.', '2331', '2331', '2231', '.221', '..21', '.221', '2321', '2321', '22.1', '2...',
  '....', '....', '....'];

export function butterflyFrames(): Frame[] {
  return [0, 1, 2, 1].map((squeeze) =>
    paint((x, y) => {
      const half = WING[y] ?? '....';
      const c = x < 4 ? x : 7 - x;
      if (c === 3) return half[3] ?? '.';
      if (c < squeeze) return '.';
      const src = Math.min(2, Math.floor(((c - squeeze + 0.5) * 3) / (3 - squeeze)));
      return half[src] ?? '.';
    }),
  );
}

/** A living, boiling mass; tiles seamlessly with its neighbours. */
export function amoebaFrames(): Frame[] {
  const bubbles = [[1, 3], [5, 9], [2, 12], [6, 5], [4, 0]];
  return [0, 1, 2, 3].map((k) =>
    paint((x, y) => {
      for (const [bx = 0, by = 0] of bubbles) {
        const py = (by - k * 4 + 32) % 16;
        if (x === bx && (y === py || y === (py + 1) % 16)) return '3';
      }
      const n = hash(x, y, 7) % 100;
      const wobble = hash(x, y, 11 + k) % 100;
      if (n < 22) return '1';
      if (n < 30 && wobble < 50) return '.';
      return '2';
    }),
  );
}

/**
 * Slime: a glossy ooze in the rock colour, unlike dirt at a glance - a wavering sheen along its top
 * and bright drips sliding down through it.
 */
export function slimeFrames(): Frame[] {
  return [0, 1, 2, 3].map((k) =>
    paint((x, y) => {
      const sheen = 1 + ((x + k) % 4 === 0 ? 1 : 0);
      if (y === sheen) return '3';
      if (y === 0) return '2';
      const drip = (hash(x, 0, 3) % 16 + k * 4) % 16;
      if (x % 3 === 1 && y === drip) return '3';
      if (x % 3 === 1 && (y === drip - 1 || y === drip - 2)) return '2';
      return hash(x, y, 5) % 100 < 28 ? '1' : '2';
    }),
  );
}

/** The magic wall: brick with a shimmer running along its highlight rows. */
export function magicWallFrames(): Frame[] {
  return [0, 1, 2, 3].map((k) =>
    paint((x, y) => {
      const p = pixel(BRICK, x, y);
      if (p === '2' && y % 4 === 0 && (x + k * 2) % 8 < 2) return '3';
      if (p === '2' && (x + y + k * 3) % 11 === 0) return '3';
      return p;
    }),
  );
}

export function expandingWallFrame(): Frame {
  return paint((x, y) => {
    const p = pixel(BRICK, x, y);
    if (p === '2' && y % 4 === 0 && (x === 0 || x === 1 || x === 6 || x === 7)) return '3';
    return p;
  });
}

/** Explosion stages 1-5: a burst that swells, breaks up and scatters. */
export function explosionFrames(): Frame[] {
  const radius = [4.5, 6.5, 8.5, 8.5, 8];
  const density = [100, 92, 78, 50, 24];
  return [0, 1, 2, 3, 4].map((stage) =>
    paint((x, y) => {
      const dx = (x - 3.5) * 2;
      const dy = y - 7.5;
      const d = Math.sqrt(dx * dx + dy * dy);
      const r = radius[stage] ?? 8;
      if (d > r || hash(x, y, stage) % 100 >= (density[stage] ?? 50)) return '.';
      if (stage >= 3) return d < r * 0.4 ? '2' : '1';
      return d < r * 0.4 ? '3' : d < r * 0.72 ? '2' : '1';
    }),
  );
}

/** The cover: steel plates with a shimmer flickering over them while a cave appears and disappears. */
export function coverFrames(): Frame[] {
  return [0, 1, 2, 3].map((k) =>
    paint((x, y) => {
      const p = pixel(STEEL, x, y);
      if (p !== '2') return p;
      const h = hash(x, y, 40 + k) % 32;
      return h < 3 ? '3' : h < 6 ? '1' : '2';
    }),
  );
}
